import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Loader2, LogOut, Plus, Link2, ExternalLink, CheckCircle2, MessageSquare, Trash2, Sparkles, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';

const CONNECTABLE = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'linkedin', label: 'LinkedIn' },
];

const STATUS_BADGE = {
  scheduled: { label: 'Scheduled', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  published: { label: 'Published', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
const REVIEW_BADGE = {
  approved: { label: 'You approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  changes_requested: { label: 'Changes requested', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export const ClientPortalPage = () => {
  const { api, user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [posts, setPosts] = useState([]);
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedback, setFeedback] = useState('');

  const load = async () => {
    try {
      const [m, p, c] = await Promise.all([
        api.get('/portal/me'),
        api.get('/portal/posts'),
        api.get('/portal/connections'),
      ]);
      setMe(m.data);
      setPosts(p.data.posts || []);
      setConns(c.data.connections || []);
    } catch (e) {
      toast.error('Could not load your portal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async (platform) => {
    setBusy(platform);
    try {
      const res = await api.post(`/connections/${platform}/start`);
      sessionStorage.setItem('oauth_platform', platform);
      sessionStorage.setItem('oauth_state', res.data.state);
      sessionStorage.setItem('oauth_return', '/portal');
      window.location.href = res.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start connection');
      setBusy(null);
    }
  };

  const disconnect = async (connectionId) => {
    if (!window.confirm('Disconnect this account? Your agency will no longer be able to post to it.')) return;
    try {
      await api.delete(`/portal/connections/${connectionId}`);
      toast.success('Account disconnected');
      setConns((c) => c.filter((x) => x.connection_id !== connectionId));
    } catch (e) {
      toast.error('Could not disconnect');
    }
  };

  const respond = async (postId, decision, fb = null) => {
    try {
      await api.post(`/portal/posts/${postId}/respond`, { decision, feedback: fb });
      toast.success(decision === 'approved' ? 'Post approved' : 'Changes requested — your agency was notified');
      setPosts((ps) => ps.map((p) => (p.id === postId ? { ...p, review_status: decision, review_comment: fb } : p)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit your response');
    }
  };

  const submitFeedback = async () => {
    if (!feedbackFor) return;
    await respond(feedbackFor, 'changes_requested', feedback.trim() || null);
    setFeedbackFor(null);
    setFeedback('');
  };

  const brandColor = me?.client?.brand_color || '#4f46e5';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="client-portal-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {me?.client?.logo_url ? (
              <img src={me.client.logo_url} alt="brand" className="w-9 h-9 rounded-lg object-contain border border-zinc-100" />
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: brandColor }}>
                <Sparkles className="w-5 h-5" />
              </div>
            )}
            <div>
              <p className="font-bold text-zinc-900 leading-tight" data-testid="portal-brand-name">{me?.client?.name || 'Your brand'}</p>
              <p className="text-xs text-zinc-500">Managed by {me?.agency_name}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="portal-logout"><LogOut className="w-4 h-4 mr-1.5" /> Sign out</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Connect accounts */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-4 h-4 text-indigo-600" />
            <h2 className="text-base font-semibold text-zinc-900">Connect your accounts</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-4">
            Connect your social accounts so your agency can publish on your behalf. You stay in control —
            disconnect anytime. You won't post here; your agency handles the posting.
          </p>
          <Card><CardContent className="p-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              {CONNECTABLE.map((p) => (
                <Button key={p.id} variant="outline" disabled={busy === p.id}
                  onClick={() => connect(p.id)} data-testid={`portal-connect-${p.id}`}>
                  {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
                  Connect {p.label}
                </Button>
              ))}
            </div>
            {conns.length > 0 && (
              <div className="divide-y divide-zinc-100 border-t border-zinc-100 pt-2" data-testid="portal-connections-list">
                {conns.map((c) => (
                  <div key={c.connection_id} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="capitalize font-medium text-zinc-700">{c.platform}</span>
                      <span className="text-zinc-400">·</span>
                      <span className="text-zinc-500">{c.account_name || 'Connected'}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700"
                      onClick={() => disconnect(c.connection_id)} data-testid={`portal-disconnect-${c.connection_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </section>

        {/* Posts */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-4 h-4 text-indigo-600" />
            <h2 className="text-base font-semibold text-zinc-900">Your posts</h2>
          </div>
          {posts.length === 0 ? (
            <Card><CardContent className="p-10 text-center text-zinc-400" data-testid="portal-posts-empty">
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-indigo-200" />
              <p className="text-sm">No scheduled or published posts yet. They'll show up here as your agency prepares them.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="portal-posts-list">
              {posts.map((p) => {
                const sb = STATUS_BADGE[p.status] || { label: p.status, cls: 'bg-zinc-100 text-zinc-600' };
                const rb = REVIEW_BADGE[p.review_status];
                const media = p.media?.[0];
                return (
                  <Card key={p.id} data-testid={`portal-post-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {media && (
                          <img src={media.url} alt="" className="w-20 h-20 rounded-md object-cover border border-zinc-100 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge variant="outline" className="capitalize text-[10px]">{p.platform}</Badge>
                            <Badge variant="outline" className={`text-[10px] ${sb.cls}`}>{sb.label}</Badge>
                            {rb && <Badge variant="outline" className={`text-[10px] ${rb.cls}`}>{rb.label}</Badge>}
                          </div>
                          {p.post_title && <p className="font-semibold text-sm text-zinc-800 truncate">{p.post_title}</p>}
                          <p className="text-sm text-zinc-600 line-clamp-3 whitespace-pre-wrap">{p.caption}</p>
                          {p.scheduled_for && p.status === 'scheduled' && (
                            <p className="text-[11px] text-zinc-400 mt-1">Scheduled for {new Date(p.scheduled_for).toLocaleString()}</p>
                          )}
                          {p.review_comment && (
                            <p className="text-[11px] text-rose-600 mt-1">Your note: {p.review_comment}</p>
                          )}
                          <div className="flex items-center gap-2 mt-3">
                            {p.status === 'published' && p.published_url && (
                              <a href={p.published_url} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-indigo-600 hover:underline flex items-center gap-1" data-testid={`portal-viewlive-${p.id}`}>
                                <ExternalLink className="w-3.5 h-3.5" /> View live
                              </a>
                            )}
                            {p.status === 'scheduled' && p.review_status !== 'approved' && (
                              <>
                                <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => respond(p.id, 'approved')} data-testid={`portal-approve-${p.id}`}>
                                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" className="h-7"
                                  onClick={() => { setFeedbackFor(p.id); setFeedback(''); }} data-testid={`portal-changes-${p.id}`}>
                                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> Request changes
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <Dialog open={!!feedbackFor} onOpenChange={(o) => !o && setFeedbackFor(null)}>
        <DialogContent data-testid="portal-feedback-dialog">
          <DialogHeader><DialogTitle>Request changes</DialogTitle></DialogHeader>
          <Textarea rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell your agency what you'd like changed…" data-testid="portal-feedback-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFeedbackFor(null)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submitFeedback} data-testid="portal-feedback-submit">Send to agency</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortalPage;
